import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useMemo, useState, type ReactNode } from "react";
import { Pencil, Trash2 } from "lucide-react";
import type { MatchSummary } from "@/lib/types";
import { useMatches } from "@/features/matches/useMatches";
import {
  useCoachSummariesList,
  useMatchSummaryMutations,
} from "./hooks/useCoachSummariesList";
import { MatchSummaryComposerModal } from "./components/MatchSummaryComposerModal";
import { PageHeader } from "./components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import { linkedEvidenceLabel, visibilityLabel } from "./feedbackLibraryLabels";

/**
 * Coach > Match Summaries list page (M6a).
 *
 * Ports the legacy `renderCoachMatchSummaries`
 * (`js/coaching/match-summaries.js`): the team's match summaries,
 * each with the match label, visibility + linked-evidence counts,
 * a short recap preview, and per-row Edit / Delete.
 *
 * Like Playlists, the **composer is its own modal**
 * (`<MatchSummaryComposerModal>`) — there's no Coach Review
 * surface for summaries. "+ New summary" opens it in create mode.
 */

export function MatchSummariesPage() {
  const toast = useToast();
  const summariesQuery = useCoachSummariesList();
  const matchesQuery = useMatches();
  const mutations = useMatchSummaryMutations();

  const summaries = summariesQuery.data?.summaries ?? [];

  const matchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of matchesQuery.data ?? []) {
      const teams = `${m.home_team} vs ${m.away_team}`;
      map.set(m.id, m.date ? `${teams} · ${m.date}` : teams);
    }
    return map;
  }, [matchesQuery.data]);

  const collection = useCollection(summaries, {
    scope: "MatchSummariesPage.tsx",
    text: (item) =>
      `${matchLabelById.get(item.match_id) ?? item.match_id} ${item.team_positives} ${item.team_improvements} ${item.training_focus}`,
    matches: (item, visibility) => item.visibility === visibility,
  });
  const [deleting, setDeleting] = useState<number | null>(null);
  // `composer`: null = closed; { summary: null } = create;
  // { summary } = edit.
  const [composer, setComposer] = useState<{
    summary: MatchSummary | null;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MatchSummary | null>(null);

  const handleDelete = (summary: MatchSummary) => {
    setConfirmDelete(summary);
  };

  const confirmDeleteSummary = async () => {
    if (!confirmDelete) return;
    const summary = confirmDelete;
    setConfirmDelete(null);
    setDeleting(summary.id);
    try {
      await mutations.deleteSummary(summary.id);
      toast.success("Match recap deleted");
    } catch (err) {
      toast.error("Couldn't delete match recap", {
        description: errorMessage(err),
      });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="summaries-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete summary?"
          message={`Delete this match recap? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteSummary}
          onClose={() => setConfirmDelete(null)}
          data-testid="summaries-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Match Recaps"
        description="Team-level feedback that turns a match into positives, improvements, training focus, and linked evidence."
        actions={
          <Button
            type="button"
            onClick={() => setComposer({ summary: null })}
            data-testid="summaries-new-summary"
            aria-label="New recap"
            title="New recap"
            size="sm"
            className="library-create-action"
          >
            New recap
          </Button>
        }
      />

      <CollectionToolbar
        collection={collection}
        label="recaps"
        filterLabel="Visibility"
        filters={[
          { value: "", label: "All audiences" },
          { value: "team", label: "Team" },
          { value: "player", label: "Player + family" },
          { value: "coach", label: "Coaches" },
        ]}
      />
      {collection.controls && collection.filteredTotal === 0 ? (
        <p className="text-sm text-ink-muted">
          No results. Clear filters to see all items.
        </p>
      ) : null}
      {summariesQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="summaries-loading"
        >
          Loading summaries…
        </p>
      ) : summaries.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="summaries-empty"
        >
          No match recaps yet. Add a team-visible recap after a match.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="summaries-list">
          {collection.items.map((summary) => (
            <SummaryRow
              key={summary.id}
              summary={summary}
              matchLabel={
                matchLabelById.get(summary.match_id) ?? summary.match_id
              }
              deleting={deleting === summary.id}
              onEdit={() => setComposer({ summary })}
              onDelete={() => handleDelete(summary)}
            />
          ))}
        </ul>
      )}

      <CollectionPagination collection={collection} />

      {composer ? (
        <MatchSummaryComposerModal
          summary={composer.summary}
          mutations={mutations}
          onClose={() => setComposer(null)}
        />
      ) : null}
    </div>
  );
}

type SummaryRowProps = {
  summary: MatchSummary;
  matchLabel: string;
  deleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

function SummaryRow({
  summary,
  matchLabel,
  deleting,
  onEdit,
  onDelete,
}: SummaryRowProps) {
  const noteCount = summary.note_ids?.length ?? 0;
  const clipCount = summary.clip_ids?.length ?? 0;
  const playlistCount = summary.playlist_ids?.length ?? 0;
  const metaParts = [
    "For: team",
    `Player sees: ${visibilityLabel(summary.visibility)}`,
    linkedEvidenceLabel({
      notes: noteCount,
      clips: clipCount,
      playlists: playlistCount,
    }),
    "Next: Edit recap",
  ];

  const phases: Array<{ label: string; tone: string; text: string }> = [
    {
      label: "Positives",
      tone: "border-good/40 bg-good/10 text-good",
      text: (summary.team_positives ?? "").trim(),
    },
    {
      label: "Improvements",
      tone: "border-flag/40 bg-flag/10 text-flag",
      text: (summary.team_improvements ?? "").trim(),
    },
    {
      label: "Training Focus",
      tone: "border-accent/40 bg-accent/10 text-accent",
      text: (summary.training_focus ?? "").trim(),
    },
  ];
  const bodyFallback = (summary.body ?? "").trim();

  return (
    <li
      className="flex flex-col gap-2 rounded border border-rule bg-surface p-2 shadow-sm"
      data-testid="summary-row"
      data-summary-id={summary.id}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display text-ink truncate">{matchLabel}</div>
          <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
            {metaParts.join(" · ")}
          </div>
        </div>
        <div className="inline-flex w-max shrink-0 items-center gap-0.5 rounded border border-rule bg-elevated p-0.5">
          <RowButton
            label="Edit"
            testid="summary-action-edit"
            onClick={onEdit}
            icon={<Pencil size={16} strokeWidth={2} aria-hidden="true" />}
          />
          <RowButton
            label="Delete"
            testid="summary-action-delete"
            onClick={onDelete}
            disabled={deleting}
            danger
            icon={<Trash2 size={16} strokeWidth={2} aria-hidden="true" />}
          />
        </div>
      </div>
      {phases.some((p) => p.text) ? (
        <div
          data-testid="summary-row-phases"
          className="grid grid-cols-1 gap-2 border-t border-rule pt-2 sm:grid-cols-3"
        >
          {phases.map((p) =>
            p.text ? (
              <div
                key={p.label}
                className={`border-l-2 ${p.tone} py-0.5 pl-2 text-xs`}
                data-testid={`summary-row-phase-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="font-display uppercase tracking-wider text-[0.55rem] opacity-80 mb-0.5">
                  {p.label}
                </div>
                <div className="text-ink line-clamp-3 whitespace-pre-line">
                  {p.text}
                </div>
              </div>
            ) : null,
          )}
        </div>
      ) : bodyFallback ? (
        <div className="text-ink-muted text-xs whitespace-pre-line line-clamp-2">
          {bodyFallback}
        </div>
      ) : null}
    </li>
  );
}

function RowButton(props: {
  label: string;
  testid: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:bg-destructive/10 hover:text-destructive"
          : "border-rule text-ink-muted hover:border-accent hover:bg-accent/10 hover:text-accent",
      ].join(" ")}
    >
      <span className="library-action-icon">{props.icon}</span>
      <span className="library-action-label">
        {props.label.replace(/^\+ /, "")}
      </span>
    </button>
  );
}
